import { useQuery } from "@tanstack/react-query";
import dayjs from "dayjs";

import {
  TeamMemberTaskStatus,
  type TeamMemberTasksFilter,
  type TeamMemberTaskWithTrainee,
} from "@fitness-app/data-models/entities/TeamMemberTask";

import { useUserClaims } from "~/hooks/trainer/useUserClaims";
import { useUserRole } from "~/hooks/trainer/useUserRole";
import { supabase } from "~/store/initializeStore";

export const TEAM_MEMBER_TASKS_QUERY_KEY = "team_member_tasks";

interface UseTeamMemberTasksOptions {
  filter?: TeamMemberTasksFilter;
  startDate?: string;
  endDate?: string;
  includeOverdue?: boolean;
  showCompleted?: boolean;
  traineeId?: string;
}

export const useTeamMemberTasks = (options: UseTeamMemberTasksOptions = {}) => {
  const { userId, isTeamMember, isTrainer } = useUserRole();
  const { memberId } = useUserClaims();
  const { filter, startDate, endDate, includeOverdue = true, showCompleted = false, traineeId } = options;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: [
      TEAM_MEMBER_TASKS_QUERY_KEY,
      userId,
      // memberId steruje filtrem, więc musi być w kluczu — claimy potrafią
      // dojechać po pierwszym renderze.
      memberId,
      startDate,
      endDate,
      filter,
      includeOverdue,
      showCompleted,
      traineeId,
    ],
    // Zadania na pulpicie są ZAWSZE osobiste. Fail-closed: bez memberId
    // (claimy w trakcie ładowania) nie strzelamy w ogóle, zamiast lecieć
    // bez filtra i cache'ować cudzą listę.
    enabled: Boolean(userId) && (isTeamMember || isTrainer) && Boolean(memberId),
    queryFn: async () => {
      let query = supabase
        .from("team_member_task")
        .select("*, trainee:traineeId(id, firstName, lastName, email)")
        .neq("status", TeamMemberTaskStatus.Canceled);

      // When viewing tasks for a specific trainee, show all tasks for that trainee
      // Otherwise (dashboard/drawer context), restrict to assigned tasks for non-owners
      if (traineeId) {
        query = query.eq("traineeId", traineeId);
      } else {
        // Zadania są zawsze osobiste — także dla ownera i administracji.
        query = query.eq("assignedTeamMemberId", memberId ?? "");
      }

      // Apply status filter
      if (filter?.status) {
        if (Array.isArray(filter.status)) {
          query = query.in("status", filter.status);
        } else {
          query = query.eq("status", filter.status);
        }
      }

      // Apply category filter
      if (filter?.category) {
        if (Array.isArray(filter.category)) {
          query = query.in("category", filter.category);
        } else {
          query = query.eq("category", filter.category);
        }
      }

      // Apply priority filter
      if (filter?.priority) {
        if (Array.isArray(filter.priority)) {
          query = query.in("priority", filter.priority);
        } else {
          query = query.eq("priority", filter.priority);
        }
      }

      // Apply traineeId filter
      if (filter?.traineeId !== undefined) {
        if (filter.traineeId === null) {
          query = query.is("traineeId", null);
        } else {
          query = query.eq("traineeId", filter.traineeId);
        }
      }

      // Note: JSONB date filtering done client-side for now
      // Server-side JSONB filtering requires RPC function or raw SQL

      // Apply search filter
      if (filter?.search) {
        query = query.ilike("title", `%${filter.search}%`);
      }

      // Order by priority (JSONB field ordering requires custom SQL function)
      query = query.order("priority", { ascending: false });

      const { data, error } = await query;

      if (error) {
        throw error;
      }

      // Cast to proper type - Supabase returns raw JSON types that need conversion
      return (data || []) as unknown as TeamMemberTaskWithTrainee[];
    },
  });

  // Sort tasks: those with time first (sorted by time), then those without time
  const sortTasksByTime = (tasks: TeamMemberTaskWithTrainee[]) => {
    return [...tasks].sort((a, b) => {
      const aTime = a.dueDate?.time;
      const bTime = b.dueDate?.time;

      // Tasks with time come first
      if (aTime && !bTime) return -1;
      if (!aTime && bTime) return 1;

      // Both have time - sort by time
      if (aTime && bTime) {
        return aTime.localeCompare(bTime);
      }

      // Neither has time - maintain original order (by priority from server)
      return 0;
    });
  };

  const groupTasksByDate = (tasks: TeamMemberTaskWithTrainee[]) => {
    const today = dayjs().startOf("day");
    const tomorrow = today.add(1, "day");

    const overdue: TeamMemberTaskWithTrainee[] = [];
    const todayTasks: TeamMemberTaskWithTrainee[] = [];
    const tomorrowTasks: TeamMemberTaskWithTrainee[] = [];
    const noDueDate: TeamMemberTaskWithTrainee[] = [];
    const completed: TeamMemberTaskWithTrainee[] = [];

    tasks.forEach((task) => {
      if (task.status === TeamMemberTaskStatus.Completed) {
        if (showCompleted) {
          completed.push(task);
        }
        return;
      }

      const dueDateStr = task.dueDate?.date;
      if (!dueDateStr) {
        noDueDate.push(task);
      } else {
        const dueDate = dayjs(dueDateStr).startOf("day");
        if (dueDate.isBefore(today)) {
          overdue.push(task);
        } else if (dueDate.isSame(today, "day")) {
          todayTasks.push(task);
        } else if (dueDate.isSame(tomorrow, "day")) {
          tomorrowTasks.push(task);
        }
        // Tasks beyond tomorrow are not included in Today view
      }
    });

    return {
      overdue: sortTasksByTime(overdue),
      today: sortTasksByTime(todayTasks),
      tomorrow: sortTasksByTime(tomorrowTasks),
      noDueDate,
      completed,
    };
  };

  const groupTasksByWeekDay = (tasks: TeamMemberTaskWithTrainee[], weekStart: dayjs.Dayjs) => {
    const today = dayjs().startOf("day");
    const result: Record<string, TeamMemberTaskWithTrainee[]> = {};
    const overdue: TeamMemberTaskWithTrainee[] = [];
    const noDueDate: TeamMemberTaskWithTrainee[] = [];
    const completed: TeamMemberTaskWithTrainee[] = [];

    // Initialize days of the week
    for (let i = 0; i < 7; i++) {
      const day = weekStart.add(i, "day").format("YYYY-MM-DD");
      result[day] = [];
    }

    tasks.forEach((task) => {
      if (task.status === TeamMemberTaskStatus.Completed) {
        if (showCompleted) {
          completed.push(task);
        }
        return;
      }

      const dueDateStr = task.dueDate?.date;
      if (!dueDateStr) {
        noDueDate.push(task);
        return;
      }

      const dueDate = dayjs(dueDateStr).startOf("day");
      const dateKey = dueDate.format("YYYY-MM-DD");

      // Only show as overdue if before today (not just before selected week)
      if (dueDate.isBefore(today)) {
        overdue.push(task);
      } else if (result[dateKey]) {
        result[dateKey].push(task);
      }
    });

    // Sort tasks within each day by time
    for (const day of Object.keys(result)) {
      result[day] = sortTasksByTime(result[day] ?? []);
    }

    return { byDay: result, overdue: sortTasksByTime(overdue), noDueDate, completed };
  };

  return {
    tasks: data || [],
    isLoading,
    error,
    refetch,
    groupTasksByDate,
    groupTasksByWeekDay,
  };
};
